import React, { FC, useContext } from "react";
import { LangContext, NavbarContext } from "../context";
import { BlackSemiBold, LangPrefix, StoryDescriptionKey } from "../enums";

const CEWarningGenerator = (hash: string, langPrefix: string) =>
  langPrefix === LangPrefix.Ko ? (
    <>
      <a href={hash} className="underline">
        황당엔딩
      </a>
      에 대비하여 이 스토리 전에 저장하는 것을 권장함.
    </>
  ) : langPrefix === LangPrefix.Cht ? (
    <>
      在該聊天室之前保存進度以確保迴避
      <a href={hash} className="underline">
        CE
      </a>
    </>
  ) : (
    <>
      Save before this chatroom to make sure you pass the{" "}
      <a href={hash} className="underline">
        CE
      </a>
      .
    </>
  );

const HEGenerator = (name: string) => (
  <>Condition: Have very high (almost perfect) favorability of {name}.</>
);
const HEGeneratorKo = (name: string) => (
  <>조건: 굉장히 높은 (거의 완벽한) {name}의 호감도</>
);

const HEGeneratorCht = (name: string) => (
  <>達成條件：{name}的好感度極高（接近完美）。</>
);

export interface StoryDescriptionProps {
  k?: StoryDescriptionKey;
}

export const StoryDescription: FC<StoryDescriptionProps> = ({ k }) => {
  const { color, text } = useContext(NavbarContext);
  const { langPrefix } = useContext(LangContext);
  const ko = langPrefix === LangPrefix.Ko;
  const cht = langPrefix === LangPrefix.Cht;

  switch (k) {
    case StoryDescriptionKey.None:
      return ko ? <>읽기 전용</> : cht ? <>只讀</> : <>Read Only</>;
    case StoryDescriptionKey.Progress:
      return ko ? <>공략 작성 중</> : <>In Progress</>;
    case StoryDescriptionKey.DE:
      return ko ? (
        <>조건: 많은 (약 70% 이상) 채팅방 부재중 (비쥬얼 노벨은 제외)</>
      ) : cht ? (
        <>達成條件：錯過大量（大約70%或者更多）聊天室（不包括視覺小說）。</>
      ) : (
        <>
          Condition: Miss many (about 70% or more) of the chatrooms (excluding
          Visual Novels).
        </>
      );
    case StoryDescriptionKey.EaterDE:
      return ko ? (
        <>조건: 윌리엄의 호감도 높음</>
      ) : cht ? (
        <>達成條件：William的好感度過高。</>
      ) : (
        <>Condition: High on William's favorability.</>
      );
    case StoryDescriptionKey.WilliamDE:
      return ko ? (
        <>조건: 윌리엄의 호감도 낮음</>
      ) : cht ? (
        <>達成條件：William的好感度過低。</>
      ) : (
        <>Condition: Low on William's favorability.</>
      );
    case StoryDescriptionKey.HiHE:
      return ko
        ? HEGeneratorKo("하이")
        : cht
        ? HEGeneratorCht("Hi")
        : HEGenerator("Hi");
    case StoryDescriptionKey.LeoHE:
      return ko
        ? HEGeneratorKo("리오")
        : cht
        ? HEGeneratorCht("Leo")
        : HEGenerator("Leo");
    case StoryDescriptionKey.SolHE:
      return ko
        ? HEGeneratorKo("솔")
        : cht
        ? HEGeneratorCht("Sol")
        : HEGenerator("Sol");
    case StoryDescriptionKey.EaterHE:
      return ko ? (
        <>조건: 이터의 호감도 높음</>
      ) : cht ? (
        <>達成條件：Eater的好感度極高（接近完美）。</>
      ) : (
        <>Condition: High on Eater's favorability.</>
      );
    case StoryDescriptionKey.WilliamHE:
      return ko ? (
        <>조건: 윌리엄의 호감도 높음</>
      ) : cht ? (
        <>達成條件：William的好感度極高（接近完美）。</>
      ) : (
        <>Condition: High on William's favorability.</>
      );
    case StoryDescriptionKey.Sujee:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (수지 백합엔딩)</span>.
          하이의 호감도가 낮을 때 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局（Sujee結局）</span>
          。當Hi好感度過低時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>Comical Ending (Sujee Ending)</span>.
          Occurs when you are low on Hi's favorability.
        </>
      );
    case StoryDescriptionKey.CatBusWarning:
      return CEWarningGenerator("#the-cat-becomes-too-healthy", langPrefix);
    case StoryDescriptionKey.CatBus:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (고양이버스 엔딩)</span>.{" "}
          <a href="#surprising-offer" className="underline">
            10:35 채팅방
          </a>
          에서 리오의 선택지를 적게 (2개 이하) 선택할 경우 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局（貓巴士結局）</span>。當在
          <a href="#surprising-offer" className="underline">
            10:35聊天室
          </a>
          中選擇過少Leo選項（小於等於2個）時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>Comical Ending (Cat Bus Ending)</span>
          . Occurs when you chose little (2 or less) of Leo's choices in{" "}
          <a href="#surprising-offer" className="underline">
            10:35 chat
          </a>
          .
        </>
      );
    case StoryDescriptionKey.MetaWarning:
      return ko ? (
        <>
          <a href="#william-is-so-hot" className="underline">
            황당엔딩
          </a>
          에 대비하여 이 채팅방 전에 저장하는 것을 권장함. 황당 엔딩을 피하기
          위해서는{" "}
          {color ? (text ? "표시된" : "옥색") : text ? "(*) 표시된" : "옳은"}{" "}
          선택지들을 골라야 함.
        </>
      ) : cht ? (
        <>
          在該聊天室之前保存進度以確保迴避
          <a href="#william-is-so-hot" className="underline">
            CE
          </a>
          。應選擇
          {color ? (text ? "標記的" : "青色") : text ? "(*) 標記的" : "正確的"}
          選項迴避CE。
        </>
      ) : (
        <>
          Save before this chatroom to make sure you pass the{" "}
          <a href="#william-is-so-hot" className="underline">
            CE
          </a>
          . You should choose the{" "}
          {color
            ? text
              ? "marked"
              : "cyan colored"
            : text
            ? "(*) marked"
            : "corect"}{" "}
          choices to avoid the CE.
        </>
      );
    case StoryDescriptionKey.Meta:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (원화가 엔딩)</span>.{" "}
          <a href="#do-you-remember-me" className="underline">
            20:22 채팅방
          </a>
          에서 옳은 선택지를 적게 (2개 이하) 선택할 경우 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局 （Meta結局）</span>。當在
          <a href="#do-you-remember-me" className="underline">
            20:22聊天室
          </a>
          中選擇過少正確選項（小於等於2個）時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>Comical Ending (Meta Ending)</span>.
          Occurs when you chose little (2 or less) of the correct choices in{" "}
          <a href="#do-you-remember-me" className="underline">
            20:22 chat
          </a>
          .
        </>
      );
    case StoryDescriptionKey.Rabbit:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (시계토끼 엔딩)</span>.
          리오의 호감도가 높을 때 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局（白兔結局）</span>
          。當Leo好感度過高時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>
            Comical Ending (The White Rabbit Ending)
          </span>
          . Occurs when you are high on Leo's favorability.
        </>
      );
    case StoryDescriptionKey.WizardWarning:
      return CEWarningGenerator("#you-shall-not-pass", langPrefix);
    case StoryDescriptionKey.Wizard:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (대마법사 원석 엔딩)</span>.
          <a href="#so-so" className="underline">
            11:51 채팅방
          </a>
          에서 하이의 선택지를 적게 (4개 이하) 선택할 경우 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局（大魔法師Wonseok結局）</span>
          。當在
          <a href="#so-so" className="underline">
            11:51聊天室
          </a>
          中選擇過少Hi選項（小於等於4個）時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>
            Comical Ending (The Great Wizard Wonseok Ending)
          </span>
          . Occurs when you chose little (4 or less) of Hi's choices in{" "}
          <a href="#so-so" className="underline">
            11:51 chat
          </a>
          .
        </>
      );
    case StoryDescriptionKey.MagicalWarning:
      return CEWarningGenerator(
        "#will-you-sign-a-contract-with-me",
        langPrefix
      );
    case StoryDescriptionKey.Magical:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (마법 소녀 엔딩)</span>.{" "}
          <a href="#sol-in-a-net" className="underline">
            12:03 채팅방
          </a>
          에서 솔의 선택지 3개를 모두 고를 경우 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局（魔法少女結局）</span>
          。當在
          <a href="#sol-in-a-net" className="underline">
            12:03聊天室
          </a>
          中選擇全部三個Sol選項時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>
            Comical Ending (Magic girl Ending)
          </span>
          . Occurs when you chose all three of Sol's choices in{" "}
          <a href="#sol-in-a-net" className="underline">
            12:03 chat
          </a>
          .
        </>
      );
    case StoryDescriptionKey.SphinxWarning:
      return CEWarningGenerator("#tenacious-sphinx", langPrefix);
    case StoryDescriptionKey.Sphinx:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (스핑크스 엔딩)</span>.{" "}
          <a href="#the-two-men-in-front-of-me" className="underline">
            13:45 스토리
          </a>
          에서 리오의 선택지를 적게 (2개 이하) 선택할 경우 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局（人面獅身像結局）</span>
          。當在
          <a href="#the-two-men-in-front-of-me" className="underline">
            13:45聊天室
          </a>
          中選擇過少Leo選項（小於或等於2個）時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>
            Comical Ending (The Sphinx Ending)
          </span>
          . Occurs when you chose little (2 or less) of Leo's choices in{" "}
          <a href="#the-two-men-in-front-of-me" className="underline">
            13:45 chat
          </a>
          .
        </>
      );
    case StoryDescriptionKey.JamesWedding:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (제임스 엔딩)</span>. 리오의
          호감도가 낮을 때 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局（James結局）</span>
          。當Leo好感度過低時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>Comical Ending (James' Ending)</span>.
          Occurs when you are low on Leo's favorability.
        </>
      );
    case StoryDescriptionKey.JamesRogers:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (히어로 패러디 엔딩)</span>.{" "}
          <a href="#timing" className="underline">
            13:54 채팅방
          </a>
          에서 리오의 선택지를 고르지 않을 경우 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局(惡搞英雄結局)</span>。當在
          <a href="#timing" className="underline">
            13:54聊天室
          </a>
          中不選擇Leo選項時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>
            Comical Ending (Hero Parody Ending)
          </span>
          . Occurs when you do not choose Leo's choice in{" "}
          <a href="#timing" className="underline">
            13:54 chat
          </a>
          .
        </>
      );
    case StoryDescriptionKey.JamesRogersWarning:
      return CEWarningGenerator("#our-name-is", langPrefix);
    case StoryDescriptionKey.SaiyanWarning:
      return CEWarningGenerator("#ill-show-you-my-strength", langPrefix);
    case StoryDescriptionKey.Saiyan:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (초사이어인 엔딩)</span>.{" "}
          <a href="#i-missed-you-so-i-came" className="underline">
            20:54 스토리
          </a>
          에서 솔의 선택지를 적게 (1개 이하) 선택할 경우 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局（超級賽亞人結局）</span>
          。當在
          <a href="#i-missed-you-so-i-came" className="underline">
            20:54聊天室
          </a>
          中選擇過少Sol選項（小於或等於1個）時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>
            Comical Ending (Super Saiyan Ending)
          </span>
          . Occurs when you chose little (1 or less) of Sol's choices in{" "}
          <a href="#i-missed-you-so-i-came" className="underline">
            20:54 chat
          </a>
          .
        </>
      );
    case StoryDescriptionKey.Story:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (괴담 엔딩)</span>. 하이의
          호감도가 높을 때 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局（恐怖故事結局）</span>
          。當Hi好感度過高時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>
            Comical Ending (A Creepy Story Ending)
          </span>
          . Occurs when you are high on Hi's favorability.
        </>
      );
    case StoryDescriptionKey.Siu:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (시우 엔딩)</span>. 솔의
          호감도가 낮을 때 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局（Siu結局）</span>
          。當Sol好感度過低時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>Comical Ending (Siu Ending)</span>.
          Occurs when you are low on Sol's favorability.
        </>
      );
    case StoryDescriptionKey.Puppy:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (하얀 강아지 엔딩)</span>.
          윌리엄의 호감도가 높을 때 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局（小白狗結局）</span>
          。當William好感度過高時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>
            Comical Ending (White Puppy Ending)
          </span>
          . Occurs when you are high on William's favorability.
        </>
      );
    case StoryDescriptionKey.YourName:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (너의 이름은 엔딩)</span>.
          이터의 호감도가 낮을 때 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局（你的名字結局）</span>
          。當Eater好感度過低時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>
            Comical Ending (Your Name Ending)
          </span>
          . Occurs when you are low on Eater's favorability.
        </>
      );
    case StoryDescriptionKey.Ponny:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (마이 리틀 포니 엔딩)</span>.
          하이, 리오, 솔의 호감도가 높을 때 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局（我的小馬駒結局）</span>
          。當Hi、Leo、Sol的好感度過高時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>
            Comical Ending (My Little Pony Ending)
          </span>
          . Occurs when you are high on Hi, Leo, and Sol's favorability.
        </>
      );
    case StoryDescriptionKey.Sleaper:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (슬리퍼 엔딩)</span>. 이터의
          호감도가 높을 때 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局（拖鞋結局）</span>
          。當Eater好感度過高時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>Comical Ending (Sleaper Ending)</span>
          . Occurs when you are high on Eater's favorability.
        </>
      );
    case StoryDescriptionKey.PocketmonWarning:
      return ko ? (
        <>
          <a href="#my-lady-is-mine" className="underline">
            황당엔딩
          </a>
          에 대비하여 이 채팅방 전에 저장하는 것을 권장함. 황당 엔딩을 피하기
          위해서는 {text ? "(*) 표시된" : color ? "옥색" : "옳은"} 선택지를
          골라야 함.
        </>
      ) : cht ? (
        <>
          在該聊天室之前保存進度以確保迴避
          <a href="#my-lady-is-mine" className="underline">
            CE
          </a>
          。應選擇
          {color ? (text ? "標記的" : "青色") : text ? "(*) 標記的" : "正確的"}
          選項迴避CE。
        </>
      ) : (
        <>
          Save before this chatroom to make sure you pass the{" "}
          <a href="#my-lady-is-mine" className="underline">
            CE
          </a>
          . You should choose the{" "}
          {color
            ? text
              ? "marked"
              : "cyan colored"
            : text
            ? "(*) marked"
            : "corect"}{" "}
          choice to avoid the CE.
        </>
      );
    case StoryDescriptionKey.Pocketmon:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (포켓몬 엔딩)</span>.{" "}
          <a href="#what-we-each-need-to-do" className="underline">
            18:27 스토리
          </a>
          에서 틀린 선택지를 고를 경우 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局 （寶可夢結局）</span>
          。當在
          <a href="#what-we-each-need-to-do" className="underline">
            18:27聊天室
          </a>
          中選擇了錯誤選項時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>
            Comical Ending (Pocketmon Ending)
          </span>
          . Occurs when you chose the wrong choice in{" "}
          <a href="#what-we-each-need-to-do" className="underline">
            18:27 chat
          </a>
          .
        </>
      );
    case StoryDescriptionKey.AdventureTime:
      return ko ? (
        <>
          <span className={BlackSemiBold}>황당엔딩 (어드벤처 타임 엔딩)</span>.
          이터의 호감도가 낮을 때 발생.
          <br />
          팁: 9~10일차에서 이터의 선택지를 약 13개 고르고 11~14일차에서 전부
          고를 경우 넘어가짐.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>荒誕結局（探險時光結局）</span>
          。當Eater好感度過低時觸發。
          <br />
          提示：可以通过在第9~10天选择大约13个Eater的選項并在第11~14天选择所有Eater的選項以避免CE。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>
            Comical Ending (Adventure Time Ending)
          </span>
          . Occurs when you are low on Eater's favorability.
          <br />
          Tip: Can be avoided by choosing about 13 Eater's choices in day 9~10
          and choosing all of Eater's choices in day 11~14.
        </>
      );
    case StoryDescriptionKey.ShalloteHE:
      return ko ? (
        <>
          <span className={BlackSemiBold}>해피엔딩</span>. 샤샤의 호감도가 높을
          때 (10 이상) 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>幸福結局</span>
          。當Shallote的好感度高（大於等於10）時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>Happy Ending</span>. Occurs when you
          are high (10 or more) on Shallote's favorability.
        </>
      );
    case StoryDescriptionKey.ShalloteDE:
      return ko ? (
        <>
          <span className={BlackSemiBold}>다크엔딩</span>. 샤샤의 호감도가 낮을
          때 (9 이하) 발생.
        </>
      ) : cht ? (
        <>
          <span className={BlackSemiBold}>黑暗結局</span>
          。當Shallote的好感度低（小於等於9）時觸發。
        </>
      ) : (
        <>
          <span className={BlackSemiBold}>Dark Ending</span>. Occurs when you
          are low (9 or less) on Shallote's favorability.
        </>
      );
    default:
      return null;
  }
};
